/**
 * Fechas de Reservas en la zona horaria del negocio (G375): la jornada (`service_date`) y la hora local (`local_time`)
 * vienen de la API; aquí solo se escriben («Hoy», «Ayer, martes 13 oct», «miércoles 14 de octubre») y se saca el día de
 * hoy en esa zona para el navegador del día.
 */
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const mayus = (t: string): string => t.charAt(0).toLocaleUpperCase('es') + t.slice(1);

function partes(iso: string): { y: number; m: number; d: number } {
    const [y = 1970, m = 1, d = 1] = iso.split('-').map(Number);
    return { y, m, d };
}

function fecha(iso: string): Date {
    const { y, m, d } = partes(iso);
    return new Date(Date.UTC(y, m - 1, d, 12));
}

/** El día de hoy (`AAAA-MM-DD`) en la zona del negocio. */
export function hoyEn(zona: string, ahora: Date = new Date()): string {
    try {
        return new Intl.DateTimeFormat('en-CA', { timeZone: zona, year: 'numeric', month: '2-digit', day: '2-digit' }).format(ahora);
    } catch {
        return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(ahora);
    }
}

/** La hora (`HH:MM`) de un instante en la zona del negocio. */
export function horaEn(instante: string | Date, zona: string): string {
    const d = typeof instante === 'string' ? new Date(instante) : instante;
    try {
        return new Intl.DateTimeFormat('es-ES', { timeZone: zona, hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    } catch {
        return new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    }
}

export function sumarDias(iso: string, n: number): string {
    const d = fecha(iso);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}

/** Días de `b` a `a` (positivo si `a` es después). */
export function diasEntre(a: string, b: string): number {
    return Math.round((fecha(a).getTime() - fecha(b).getTime()) / 86_400_000);
}

/** «miércoles 14 oct». */
export function diaCorto(iso: string): string {
    const d = fecha(iso);
    return `${DIAS[d.getUTCDay()] ?? ''} ${String(d.getUTCDate())} ${MESES_CORTOS[d.getUTCMonth()] ?? ''}`;
}

/** «miércoles 14 de octubre». */
export function diaLargo(iso: string): string {
    const d = fecha(iso);
    return `${DIAS[d.getUTCDay()] ?? ''} ${String(d.getUTCDate())} de ${MESES[d.getUTCMonth()] ?? ''}`;
}

/** «Hoy», «Ayer», «Mañana» o «Sáb 10 oct» (la baldosa de la hora). */
export function etiquetaDia(iso: string, hoy: string): string {
    const n = diasEntre(iso, hoy);
    if (n === 0) {
        return 'Hoy';
    }
    if (n === -1) {
        return 'Ayer';
    }
    if (n === 1) {
        return 'Mañana';
    }
    const d = fecha(iso);
    return `${mayus((DIAS[d.getUTCDay()] ?? '').slice(0, 3))} ${String(d.getUTCDate())} ${MESES_CORTOS[d.getUTCMonth()] ?? ''}`;
}

/** «Hoy, miércoles 14 oct», «Ayer, martes 13 oct» o «Sábado 10 oct» (el título de un día en 8.4). */
export function diaRelativo(iso: string, hoy: string): string {
    const n = diasEntre(iso, hoy);
    const largo = diaCorto(iso);
    if (n === 0) {
        return `Hoy, ${largo}`;
    }
    if (n === -1) {
        return `Ayer, ${largo}`;
    }
    if (n === 1) {
        return `Mañana, ${largo}`;
    }
    return mayus(largo);
}

/** «sáb 10 oct» (la línea de un cobro de no-show). */
export function diaAbreviado(iso: string): string {
    const d = fecha(iso);
    return `${(DIAS[d.getUTCDay()] ?? '').slice(0, 3)} ${String(d.getUTCDate())} ${MESES_CORTOS[d.getUTCMonth()] ?? ''}`;
}

/** «hoy a las 14:50», «ayer a las 23:10» o «el sáb 10 oct a las 21:30» (cuándo se marcó el no-show). */
export function cuandoFue(instante: string, zona: string, hoy: string): string {
    const dia = hoyEn(zona, new Date(instante));
    const hora = horaEn(instante, zona);
    const n = diasEntre(dia, hoy);
    if (n === 0) {
        return `hoy a las ${hora}`;
    }
    if (n === -1) {
        return `ayer a las ${hora}`;
    }
    return `el ${diaAbreviado(dia)} a las ${hora}`;
}
